import { create } from "zustand";
import { deriveRunFailureSummary } from "@/lib/aicheck/run-failure-summary";
import type {
  AggregatedProjectData,
  AICheckResult,
  AICheckRunResult,
  ApiProgressInfo,
  CheckLane,
  ConsistencyCheckResult,
  ContactTopicsResult,
  CustomerProfileResult,
  FailedCheckInfo,
  FailedStage,
  FocusedEvidenceTarget,
  NodeExecutionDirective,
  PartialRunData,
  PipelineNodeMembers,
  PipelineNodeRuntimeStatus,
  ProjectDataSourceDescriptor,
  RunProgress,
  RunStatus,
  ScheduledAdminControl,
  SkippedItem,
  SummaryReturnTarget,
} from "@/types/aicheck";

function formatApiStatus(p: ApiProgressInfo): string {
  if (p.attempt && p.attempt >= 2) {
    return `${p.checkName}: Retrying... (attempt ${p.attempt})`;
  }
  if (p.phase === "waiting") {
    return `${p.checkName}: Waiting for AI response...`;
  }
  if (p.tokensReceived) {
    return `${p.checkName}: Generating... (${p.tokensReceived} tokens, ${((p.bytesReceived ?? 0) / 1024).toFixed(1)} KB)`;
  }
  return `${p.checkName}: Processing...`;
}

function chooseRicherError(a: string, b: string): string {
  const score = (s: string) =>
    s.length +
    (s.includes("request_id=") ? 40 : 0) +
    (s.includes("details=") ? 40 : 0) +
    (s.includes("type=") ? 20 : 0) +
    (s.includes("code=") ? 20 : 0);
  return score(b) > score(a) ? b : a;
}

function buildSelectiveRerunProgress(
  currentProgress: RunProgress,
  baseResult: AICheckRunResult,
  checkIds: string[],
  options?: {
    skipProfile?: boolean;
    skipCompaction?: boolean;
  },
): RunProgress {
  const plannedChecks = baseResult.plannedChecks ??
    currentProgress.plannedChecks ?? [
      ...new Set(baseResult.aiResults.map((result) => result.checkName)),
    ];
  const plannedCheckNameById = {
    ...(currentProgress.plannedCheckNameById ?? {}),
    ...(baseResult.plannedCheckNameById ?? {}),
  };

  for (const result of baseResult.aiResults) {
    if (!plannedCheckNameById[result.checkId]) {
      plannedCheckNameById[result.checkId] = result.checkName;
    }
  }

  const selectedCheckIds = [...new Set(checkIds)];
  const selectedCheckNames = selectedCheckIds
    .map((checkId) => plannedCheckNameById[checkId])
    .filter((checkName): checkName is string => !!checkName);

  const checkLaneMap = baseResult.checkLaneMap ?? currentProgress.checkLaneMap;
  const afterAllCheckNames = plannedChecks.filter(
    (checkName) => checkLaneMap?.[checkName] === "afterAll",
  );
  const includesNonAfterAllTarget = selectedCheckNames.some(
    (checkName) => checkLaneMap?.[checkName] !== "afterAll",
  );
  const affectedCheckNames = new Set(selectedCheckNames);
  if (includesNonAfterAllTarget) {
    for (const checkName of afterAllCheckNames) {
      affectedCheckNames.add(checkName);
    }
  }

  const completedCheckNames = [
    ...new Set(
      baseResult.aiResults
        .filter((result) => result.status !== "skipped")
        .map((result) => result.checkName),
    ),
  ].filter((checkName) => !affectedCheckNames.has(checkName));

  const plannedCheckIds =
    baseResult.plannedCheckIds ?? currentProgress.plannedCheckIds;
  const nextPlannedCheckNameById =
    Object.keys(plannedCheckNameById).length > 0
      ? plannedCheckNameById
      : undefined;

  return {
    ...currentProgress,
    status: "running-ai",
    currentStep: "Re-running selected checks...",
    completedChecks: completedCheckNames.length,
    totalChecks: plannedChecks.length,
    error: undefined,
    apiStatus: undefined,
    apiProgress: undefined,
    activeChecks: undefined,
    plannedChecks,
    completedCheckNames,
    failedStage: undefined,
    failedChecks: undefined,
    stageError: undefined,
    skippedItems: undefined,
    checkLaneMap,
    checkBatchGroups:
      baseResult.checkBatchGroups ?? currentProgress.checkBatchGroups,
    consistencyRuleResults:
      baseResult.independentConsistencyRules ??
      currentProgress.consistencyRuleResults,
    profileConsistencyRuleResults:
      baseResult.profileConsistencyRules ??
      currentProgress.profileConsistencyRuleResults,
    plannedCheckIds,
    plannedCheckNameById: nextPlannedCheckNameById,
    lastRecoveryError: undefined,
    graphSkeleton: baseResult.graphSkeleton ?? currentProgress.graphSkeleton,
    nodeMembers: baseResult.nodeMembers ?? currentProgress.nodeMembers,
    scheduledAdminControl: undefined,
    selectiveRerun: {
      selectedCheckIds,
      affectedCheckNames: [...affectedCheckNames],
      preserveFetch: true,
      preserveIndependentConsistency: true,
      preserveCompaction: options?.skipCompaction ?? true,
      preserveProfile: options?.skipProfile ?? true,
      preserveProfileConsistency: options?.skipProfile ?? true,
    },
  };
}

export interface AICheckState {
  projectId: string;
  runProgress: RunProgress;
  /**
   * ⑤ ライブ実況ログ（表示専用・非永続）。runProgress とは**別フィールド**にして、
   * 実況追記が runProgress の identity を変えないようにする（進捗グラフの過剰再描画・
   * #185 を避けるため）。
   */
  liveLog: string[];
  currentResult: AICheckRunResult | null;
  previousResult: AICheckRunResult | null;
  partialRunData: PartialRunData | null;
  focusedSummaryFindingIndex: number | null;
  focusedCheckId: string | null;
  focusedEvidenceTarget: FocusedEvidenceTarget | null;
  highlightCheckId: string | null;
  highlightEvidenceTarget: FocusedEvidenceTarget | null;
  summaryReturnTarget: SummaryReturnTarget | null;
  setProjectId: (id: string) => void;
  setRunStatus: (status: RunStatus, currentStep?: string) => void;
  setProgress: (completed: number, total: number) => void;
  setError: (error: string) => void;
  setResult: (result: AICheckRunResult) => void;
  setStartedAt: (ts: number) => void;
  setApiStatus: (status: string | undefined) => void;
  setApiProgress: (progress: ApiProgressInfo | undefined) => void;
  addPlannedChecks: (
    checks: string[],
    lane?: CheckLane,
    checkIds?: string[],
  ) => void;
  markCompletedByCheckIds: (ids: string[]) => void;
  setCheckBatchGroups: (
    groups: Array<{ id: string; name: string; checks: string[] }>,
  ) => void;
  setGraphSkeleton: (skeleton: RunProgress["graphSkeleton"]) => void;
  setNodeMembers: (nodeMembers: PipelineNodeMembers | undefined) => void;
  setNodeExecutionState: (snapshot: {
    nodeStates?: Record<string, PipelineNodeRuntimeStatus>;
    nodeExecutionDirectives?: NodeExecutionDirective[];
    nodeControlVersion?: number;
  }) => void;
  /** 実行中の状態表示を明示的に捨てる（保存済み結果の表示へ切り替えるとき）。 */
  clearNodeExecutionState: () => void;
  mergeNodeStates: (updates: Record<string, PipelineNodeRuntimeStatus>) => void;
  setScheduledAdminControl: (
    scheduledAdminControl: ScheduledAdminControl | null | undefined,
  ) => void;
  resetForSelectedRerun: (params?: {
    baseResult?: AICheckRunResult | null;
    checkIds?: string[];
    skipProfile?: boolean;
    skipCompaction?: boolean;
  }) => void;
  removeActiveCheck: (checkName: string) => void;
  initPartialRun: (projectId: number, startedAt: string) => void;
  setPartialProjectData: (
    data: AggregatedProjectData,
    source?: ProjectDataSourceDescriptor,
  ) => void;
  addPartialCheckResult: (result: AICheckResult) => void;
  /** ⑤ ライブ実況ログに1行追加（表示専用・末尾30件保持）。 */
  appendLiveLog: (line: string) => void;
  addPartialConsistencyResults: (results: ConsistencyCheckResult[]) => void;
  addProfileConsistencyResults: (results: ConsistencyCheckResult[]) => void;
  setPartialProfileResult: (result: CustomerProfileResult) => void;
  setPartialContactTopicsResult: (result: ContactTopicsResult) => void;
  addFailedCheck: (info: FailedCheckInfo) => void;
  setFailedStage: (stage: FailedStage, error?: string) => void;
  setSkippedItems: (items: SkippedItem[]) => void;
  completeRun: (result: AICheckRunResult) => void;
  setAborted: () => void;
  setCancelled: () => void;
  startRerun: () => void;
  reset: () => void;
  setReconnecting: () => void;
  setDisconnected: (lastRecoveryError?: string) => void;
  addPlannedCheckIds: (ids: string[]) => void;
  setFocusedSummaryFindingIndex: (index: number | null) => void;
  setFocusedCheckId: (id: string | null) => void;
  setFocusedEvidenceTarget: (target: FocusedEvidenceTarget | null) => void;
  setHighlightCheckId: (id: string | null) => void;
  setHighlightEvidenceTarget: (target: FocusedEvidenceTarget | null) => void;
  setSummaryReturnTarget: (target: SummaryReturnTarget | null) => void;
}

const initialProgress: RunProgress = {
  status: "idle",
  currentStep: "",
  completedChecks: 0,
  totalChecks: 0,
  startedAt: undefined,
};

export const useAICheckStore = create<AICheckState>((set) => ({
  projectId: "",
  runProgress: initialProgress,
  liveLog: [],
  currentResult: null,
  previousResult: null,
  partialRunData: null,
  focusedSummaryFindingIndex: null,
  focusedCheckId: null,
  focusedEvidenceTarget: null,
  highlightCheckId: null,
  highlightEvidenceTarget: null,
  summaryReturnTarget: null,

  setProjectId: (id) => set({ projectId: id }),

  setRunStatus: (status, currentStep) =>
    set((state) => ({
      runProgress: {
        ...state.runProgress,
        status,
        currentStep: currentStep ?? state.runProgress.currentStep,
        error: undefined,
      },
    })),

  setProgress: (completed, total) =>
    set((state) => ({
      runProgress: {
        ...state.runProgress,
        completedChecks: completed,
        totalChecks: total,
      },
    })),

  setError: (error) =>
    set((state) => ({
      currentResult: state.previousResult ?? state.currentResult,
      previousResult: null,
      partialRunData: state.partialRunData,
      focusedSummaryFindingIndex: null,
      focusedCheckId: null,
      focusedEvidenceTarget: null,
      highlightCheckId: null,
      highlightEvidenceTarget: null,
      summaryReturnTarget: null,
      runProgress: {
        ...state.runProgress,
        status: "error",
        error,
      },
    })),

  setResult: (result) =>
    set({
      currentResult: result,
      previousResult: null,
      focusedSummaryFindingIndex: null,
      focusedCheckId: null,
      focusedEvidenceTarget: null,
      highlightCheckId: null,
      highlightEvidenceTarget: null,
      summaryReturnTarget: null,
    }),

  setStartedAt: (ts) =>
    set((state) => ({
      runProgress: { ...state.runProgress, startedAt: ts },
    })),

  setApiStatus: (apiStatus) =>
    set((state) => ({
      runProgress: { ...state.runProgress, apiStatus },
    })),

  setApiProgress: (apiProgress) =>
    set((state) => {
      if (!apiProgress) {
        return {
          runProgress: {
            ...state.runProgress,
            apiProgress: undefined,
            // activeChecks is NOT cleared here — it is managed by
            // removeActiveCheck() (individual) and reset() (full clear).
            apiStatus: undefined,
          },
        };
      }
      const current = state.runProgress.activeChecks ?? [];
      const idx = current.findIndex(
        (c) => c.checkName === apiProgress.checkName,
      );
      const updated =
        idx >= 0
          ? current.map((c, i) => (i === idx ? apiProgress : c))
          : [...current, apiProgress];
      return {
        runProgress: {
          ...state.runProgress,
          apiProgress,
          activeChecks: updated,
          apiStatus: formatApiStatus(apiProgress),
        },
      };
    }),

  addPlannedChecks: (checks, lane?, checkIds?) =>
    set((state) => {
      const prevChecks = state.runProgress.plannedChecks ?? [];
      const prevIds = state.runProgress.plannedCheckIds ?? [];
      const nextChecks = [...prevChecks];
      const nextIds = [...prevIds];
      const existingCheckNames = new Set(prevChecks);
      const existingCheckIds = new Set(prevIds);
      const nameById: Record<string, string> = {
        ...(state.runProgress.plannedCheckNameById ?? {}),
      };
      if (
        checkIds &&
        checkIds.length === checks.length &&
        checkIds.length > 0
      ) {
        for (let i = 0; i < checkIds.length; i++) {
          const id = checkIds[i];
          const name = checks[i];
          if (id && name) nameById[id] = name;
          if (
            !id ||
            !name ||
            existingCheckIds.has(id) ||
            existingCheckNames.has(name)
          ) {
            continue;
          }
          existingCheckIds.add(id);
          existingCheckNames.add(name);
          nextChecks.push(name);
          nextIds.push(id);
        }
      } else {
        for (const name of checks) {
          if (!name || existingCheckNames.has(name)) continue;
          existingCheckNames.add(name);
          nextChecks.push(name);
        }
      }
      return {
        runProgress: {
          ...state.runProgress,
          plannedChecks: nextChecks,
          plannedCheckIds: nextIds.length > 0 ? nextIds : undefined,
          plannedCheckNameById:
            Object.keys(nameById).length > 0 ? nameById : undefined,
          checkLaneMap: lane
            ? {
                ...state.runProgress.checkLaneMap,
                ...Object.fromEntries(checks.map((n) => [n, lane])),
              }
            : state.runProgress.checkLaneMap,
        },
      };
    }),

  markCompletedByCheckIds: (ids) =>
    set((state) => {
      const idMap = state.runProgress.plannedCheckNameById ?? {};
      const completed = state.runProgress.completedCheckNames ?? [];
      const completedSet = new Set(completed);
      const currentActive = state.runProgress.activeChecks ?? [];
      const names = ids
        .map((id) => idMap[id])
        .filter((name): name is string => !!name);

      for (const name of names) {
        completedSet.add(name);
      }

      const namesSet = new Set(names);
      const filteredActive = currentActive.filter(
        (c) => !namesSet.has(c.checkName),
      );

      const currentApi = state.runProgress.apiProgress;
      const newApiProgress =
        currentApi && namesSet.has(currentApi.checkName)
          ? (filteredActive[0] ?? undefined)
          : currentApi;

      return {
        runProgress: {
          ...state.runProgress,
          completedCheckNames: [...completedSet],
          activeChecks: filteredActive.length > 0 ? filteredActive : undefined,
          apiProgress: newApiProgress,
          apiStatus: newApiProgress
            ? formatApiStatus(newApiProgress)
            : undefined,
        },
      };
    }),

  setCheckBatchGroups: (groups) =>
    set((state) => ({
      runProgress: {
        ...state.runProgress,
        checkBatchGroups: groups,
      },
    })),

  setGraphSkeleton: (graphSkeleton) =>
    set((state) => ({
      runProgress: {
        ...state.runProgress,
        graphSkeleton,
      },
    })),

  setNodeMembers: (nodeMembers) =>
    set((state) => ({
      runProgress: {
        ...state.runProgress,
        nodeMembers,
      },
    })),

  setNodeExecutionState: (snapshot) =>
    set((state) => ({
      runProgress: {
        ...state.runProgress,
        nodeStates: snapshot.nodeStates ?? state.runProgress.nodeStates,
        nodeExecutionDirectives:
          snapshot.nodeExecutionDirectives ??
          state.runProgress.nodeExecutionDirectives,
        nodeControlVersion:
          snapshot.nodeControlVersion ?? state.runProgress.nodeControlVersion,
      },
    })),

  // **消すのは意図したときだけ。** 空のマップを「現在値」として渡す形にすると
  // 「知らない」と「何も無い」が混ざる（→ authoritativeNodeStates）。
  clearNodeExecutionState: () =>
    set((state) => ({
      runProgress: {
        ...state.runProgress,
        nodeStates: {},
        nodeExecutionDirectives: [],
        nodeControlVersion: 0,
      },
    })),

  mergeNodeStates: (updates) =>
    set((state) => ({
      runProgress: {
        ...state.runProgress,
        nodeStates: {
          ...(state.runProgress.nodeStates ?? {}),
          ...updates,
        },
      },
    })),

  setScheduledAdminControl: (scheduledAdminControl) =>
    set((state) => ({
      runProgress: {
        ...state.runProgress,
        scheduledAdminControl:
          scheduledAdminControl === undefined ? null : scheduledAdminControl,
      },
    })),

  resetForSelectedRerun: (params) =>
    set((state) => ({
      focusedSummaryFindingIndex: null,
      focusedCheckId: null,
      focusedEvidenceTarget: null,
      highlightCheckId: null,
      highlightEvidenceTarget: null,
      summaryReturnTarget: null,
      runProgress:
        params?.baseResult && (params.checkIds?.length ?? 0) > 0
          ? buildSelectiveRerunProgress(
              state.runProgress,
              params.baseResult,
              params.checkIds ?? [],
              {
                skipProfile: params.skipProfile,
                skipCompaction: params.skipCompaction,
              },
            )
          : {
              ...state.runProgress,
              plannedChecks: [],
              completedCheckNames: [],
              checkLaneMap: undefined,
              checkBatchGroups: undefined,
              graphSkeleton: undefined,
              activeChecks: undefined,
              failedChecks: undefined,
              skippedItems: undefined,
              failedStage: undefined,
              stageError: undefined,
              plannedCheckIds: undefined,
              plannedCheckNameById: undefined,
              nodeMembers: undefined,
              nodeStates: undefined,
              nodeExecutionDirectives: undefined,
              nodeControlVersion: undefined,
              scheduledAdminControl: undefined,
              lastRecoveryError: undefined,
              selectiveRerun: undefined,
            },
    })),

  removeActiveCheck: (checkName) =>
    set((state) => {
      const current = state.runProgress.activeChecks ?? [];
      const filtered = current.filter((c) => c.checkName !== checkName);
      const newApiProgress =
        state.runProgress.apiProgress?.checkName === checkName
          ? (filtered[0] ?? undefined)
          : state.runProgress.apiProgress;
      const completed = state.runProgress.completedCheckNames ?? [];
      return {
        runProgress: {
          ...state.runProgress,
          activeChecks: filtered.length > 0 ? filtered : undefined,
          completedCheckNames: completed.includes(checkName)
            ? completed
            : [...completed, checkName],
          apiProgress: newApiProgress,
          apiStatus: newApiProgress
            ? formatApiStatus(newApiProgress)
            : undefined,
        },
      };
    }),

  initPartialRun: (projectId, startedAt) =>
    set(() => ({
      partialRunData: {
        projectId,
        startedAt,
        projectData: null,
        aiResults: [],
        consistencyResults: [],
        customerProfileResult: null,
        contactTopicsResult: null,
      },
      // ⑤ 新しい run の開始で実況ログをクリアする（別フィールド）。
      liveLog: [],
    })),

  setPartialProjectData: (data, source) =>
    set((state) => ({
      partialRunData: state.partialRunData
        ? {
            ...state.partialRunData,
            projectData: data,
            projectDataSource: source,
          }
        : null,
    })),

  addPartialCheckResult: (result) =>
    set((state) => {
      if (!state.partialRunData) return {};
      if (
        state.partialRunData.aiResults.some((r) => r.checkId === result.checkId)
      ) {
        console.warn("[store] Duplicate check-result ignored:", result.checkId);
        return {};
      }
      return {
        partialRunData: {
          ...state.partialRunData,
          aiResults: [...state.partialRunData.aiResults, result],
        },
      };
    }),

  appendLiveLog: (line) =>
    set((state) => {
      const trimmed = line.trim();
      if (!trimmed) return {};
      // runProgress とは別フィールドを更新（進捗グラフを揺らさない）。末尾30件保持。
      return { liveLog: [...state.liveLog, trimmed].slice(-30) };
    }),

  addPartialConsistencyResults: (results) =>
    set((state) => {
      if (!state.partialRunData) return {};
      const existing = new Set(
        state.partialRunData.consistencyResults.map((r) => r.ruleId),
      );
      const newResults = results.filter((r) => !existing.has(r.ruleId));
      if (newResults.length === 0) return {};
      const allResults = [
        ...state.partialRunData.consistencyResults,
        ...newResults,
      ];
      return {
        partialRunData: {
          ...state.partialRunData,
          consistencyResults: allResults,
        },
        runProgress: {
          ...state.runProgress,
          consistencyRuleResults: allResults.map((r) => ({
            name: r.ruleName,
            passed: r.passed,
          })),
        },
      };
    }),

  addProfileConsistencyResults: (results) =>
    set((state) => {
      if (!state.partialRunData) return {};
      const existing = new Set(
        state.partialRunData.consistencyResults.map((r) => r.ruleId),
      );
      const newResults = results.filter((r) => !existing.has(r.ruleId));
      if (newResults.length === 0) return {};
      const allResults = [
        ...state.partialRunData.consistencyResults,
        ...newResults,
      ];
      return {
        partialRunData: {
          ...state.partialRunData,
          consistencyResults: allResults,
        },
        runProgress: {
          ...state.runProgress,
          profileConsistencyRuleResults: newResults.map((r) => ({
            name: r.ruleName,
            passed: r.passed,
          })),
        },
      };
    }),

  setPartialProfileResult: (result) =>
    set((state) => ({
      partialRunData: state.partialRunData
        ? { ...state.partialRunData, customerProfileResult: result }
        : null,
    })),

  setPartialContactTopicsResult: (result) =>
    set((state) => ({
      partialRunData: state.partialRunData
        ? { ...state.partialRunData, contactTopicsResult: result }
        : null,
    })),

  addFailedCheck: (info) =>
    set((state) => {
      const existing = state.runProgress.failedChecks ?? [];
      const idx = existing.findIndex((e) => e.checkId === info.checkId);
      if (idx === -1) {
        return {
          runProgress: {
            ...state.runProgress,
            failedChecks: [...existing, info],
          },
        };
      }
      const current = existing[idx];
      const merged = {
        ...current,
        error: chooseRicherError(current.error, info.error),
        rerunCheckIds: [
          ...new Set([
            ...(current.rerunCheckIds ?? []),
            ...(info.rerunCheckIds ?? []),
          ]),
        ],
      };
      const next = [...existing];
      next[idx] = merged;
      return {
        runProgress: {
          ...state.runProgress,
          failedChecks: next,
        },
      };
    }),

  setFailedStage: (stage, error) =>
    set((state) => ({
      runProgress: {
        ...state.runProgress,
        failedStage: state.runProgress.failedStage ?? stage,
        stageError: state.runProgress.stageError ?? error,
      },
    })),

  setSkippedItems: (items) =>
    set((state) => ({
      runProgress: {
        ...state.runProgress,
        skippedItems: [...(state.runProgress.skippedItems ?? []), ...items],
      },
    })),

  completeRun: (result) =>
    set((state) => {
      const { failedChecks, failedStage, stageError } =
        deriveRunFailureSummary(result);
      const plannedChecks = result.plannedChecks ?? [];
      const plannedCheckNameById = result.plannedCheckNameById ?? {};
      const plannedCheckIdByName = new Map<string, string>();
      for (const [id, name] of Object.entries(plannedCheckNameById)) {
        if (id && name) plannedCheckIdByName.set(name, id);
      }
      const completedCheckNames = [
        ...new Set(
          result.aiResults
            .filter((r) => r.status !== "skipped")
            .map((r) => r.checkName),
        ),
      ];
      const skippedNameSet = new Set<string>(
        (state.runProgress.skippedItems ?? []).map((s) => s.name),
      );
      for (const r of result.aiResults) {
        if (r.status === "skipped") skippedNameSet.add(r.checkName);
      }
      for (const name of plannedChecks) {
        if (!completedCheckNames.includes(name)) skippedNameSet.add(name);
      }
      const skippedItems =
        skippedNameSet.size > 0
          ? [...skippedNameSet].map((name) => {
              const matchedId = plannedCheckIdByName.get(name) ?? name;
              return { id: matchedId, name, kind: "ai-check" as const };
            })
          : undefined;
      return {
        currentResult: result,
        previousResult: null,
        partialRunData: null,
        focusedSummaryFindingIndex: null,
        focusedCheckId: null,
        focusedEvidenceTarget: null,
        highlightCheckId: null,
        highlightEvidenceTarget: null,
        summaryReturnTarget: null,
        runProgress: {
          ...state.runProgress,
          status: result.status,
          currentStep:
            result.status === "error"
              ? "Error"
              : result.status === "cancelled"
                ? "Cancelled"
                : "Complete",
          activeChecks: undefined,
          apiProgress: undefined,
          apiStatus: undefined,
          plannedChecks,
          completedCheckNames,
          completedChecks: completedCheckNames.length,
          totalChecks: plannedChecks.length,
          checkLaneMap: result.checkLaneMap,
          checkBatchGroups: result.checkBatchGroups,
          consistencyRuleResults: result.independentConsistencyRules,
          profileConsistencyRuleResults: result.profileConsistencyRules,
          graphSkeleton: result.graphSkeleton,
          nodeMembers: result.nodeMembers,
          nodeStates: state.runProgress.nodeStates,
          nodeExecutionDirectives: undefined,
          nodeControlVersion: state.runProgress.nodeControlVersion,
          failedChecks,
          skippedItems,
          failedStage,
          stageError,
          plannedCheckIds: result.plannedCheckIds,
          plannedCheckNameById: result.plannedCheckNameById,
          scheduledAdminControl: undefined,
          lastRecoveryError: undefined,
          selectiveRerun: undefined,
        },
      };
    }),

  setCancelled: () =>
    set((state) => ({
      currentResult: state.previousResult ?? state.currentResult,
      previousResult: null,
      partialRunData: null,
      focusedSummaryFindingIndex: null,
      focusedCheckId: null,
      focusedEvidenceTarget: null,
      highlightCheckId: null,
      highlightEvidenceTarget: null,
      summaryReturnTarget: null,
      runProgress: {
        ...initialProgress,
        status: "cancelled",
        currentStep: "Check cancelled",
        checkLaneMap: undefined,
        graphSkeleton: undefined,
        plannedCheckNameById: undefined,
        nodeMembers: undefined,
        nodeStates: undefined,
        nodeExecutionDirectives: undefined,
        nodeControlVersion: undefined,
        scheduledAdminControl: undefined,
        selectiveRerun: undefined,
      },
    })),

  setAborted: () =>
    set((state) => ({
      currentResult: state.currentResult,
      previousResult: state.previousResult,
      partialRunData: state.partialRunData,
      focusedSummaryFindingIndex: null,
      focusedCheckId: null,
      focusedEvidenceTarget: null,
      highlightCheckId: null,
      highlightEvidenceTarget: null,
      summaryReturnTarget: null,
      runProgress: {
        ...state.runProgress,
        status: "aborted",
        currentStep: "Check aborted. Resume available.",
        activeChecks: undefined,
        apiProgress: undefined,
        apiStatus: undefined,
        nodeExecutionDirectives: undefined,
        scheduledAdminControl: undefined,
      },
    })),

  // **表示をリセットする側が、経過時間の起点も持つ。**
  // ここは「この run の表示をここから組み直す」という宣言なので、
  // 起点を決めるのも同じ場所であるべき。別々にしていたため、開始経路
  // （runCheck / runSelectedChecks）でしか起点が入らず、再開・再接続では
  // 経過時間も所要時間の目安も丸ごと出なかった。
  startRerun: () =>
    set((state) => ({
      runProgress: {
        ...initialProgress,
        startedAt: Date.now(),
        status: "fetching-data" as const,
        currentStep: "Fetching project data...",
        graphSkeleton: undefined,
        nodeMembers: undefined,
        nodeStates: undefined,
        nodeExecutionDirectives: undefined,
        nodeControlVersion: undefined,
        scheduledAdminControl: undefined,
        selectiveRerun: undefined,
      },
      focusedSummaryFindingIndex: null,
      focusedCheckId: null,
      focusedEvidenceTarget: null,
      highlightCheckId: null,
      highlightEvidenceTarget: null,
      summaryReturnTarget: null,
      previousResult: state.currentResult ?? state.previousResult,
      currentResult: null,
      partialRunData: null,
    })),

  reset: () =>
    set({
      runProgress: initialProgress,
      liveLog: [],
      currentResult: null,
      previousResult: null,
      partialRunData: null,
      focusedSummaryFindingIndex: null,
      focusedCheckId: null,
      focusedEvidenceTarget: null,
      highlightCheckId: null,
      highlightEvidenceTarget: null,
      summaryReturnTarget: null,
    }),

  setReconnecting: () =>
    set((state) => ({
      runProgress: {
        ...state.runProgress,
        status: "reconnecting" as const,
        currentStep: "Resuming incomplete checks...",
      },
    })),

  setDisconnected: (lastRecoveryError?) =>
    set((state) => ({
      runProgress: {
        ...state.runProgress,
        status: "disconnected" as const,
        currentStep: "Connection lost. Resume available.",
        lastRecoveryError: lastRecoveryError ?? null,
      },
    })),

  addPlannedCheckIds: (ids) =>
    set((state) => ({
      runProgress: {
        ...state.runProgress,
        plannedCheckIds: [
          ...new Set([...(state.runProgress.plannedCheckIds ?? []), ...ids]),
        ],
      },
    })),

  setFocusedSummaryFindingIndex: (index) =>
    set({ focusedSummaryFindingIndex: index }),

  setFocusedCheckId: (id) => set({ focusedCheckId: id }),

  setFocusedEvidenceTarget: (target) => set({ focusedEvidenceTarget: target }),

  setHighlightCheckId: (id) => set({ highlightCheckId: id }),

  setHighlightEvidenceTarget: (target) =>
    set({ highlightEvidenceTarget: target }),

  setSummaryReturnTarget: (target) => set({ summaryReturnTarget: target }),
}));
